import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { subscriptionAPI } from '../services/api';

export interface User {
  id: number;
  name: string;
  email: string;
  current_org_id?: number;
  current_asset_id?: number;
  organizations?: Organization[];
}

export interface Organization {
  id: number;
  name: string;
  role: string;
  is_default: boolean;
}

export interface Asset {
  id: number;
  name: string;
  description?: string;
  organisation_id?: number;
  platform_website: boolean;
  platform_mobile_app: boolean;
  platform_desktop_app: boolean;
  platform_iot: boolean;
  platform_endpoints?: boolean;
  tracking_id: string;
  api_key: string;
  is_active: boolean;
  is_verified: boolean;
  verified_at?: string;
  last_ping_at?: string;
  website_urls?: string[];
  integration_script_url?: string;
  integration_code?: string;
}

interface AuthContextType {
  user: User | null;
  assets: Asset[];
  currentAsset: Asset | null;
  organizations: Organization[];
  currentOrganization: Organization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  subscriptionStatus: string | null;
  pendingPlanId: number | null;
  pendingPeriod: string | null;
  login: (userData: User, token: string) => void;
  logout: () => void;
  setAssets: (assets: Asset[]) => void;
  setCurrentAsset: (asset: Asset) => void;
  setOrganizations: (orgs: Organization[]) => void;
  setCurrentOrganization: (org: Organization) => void;
  setSubscriptionStatus: (status: string | null, planId?: number | null, period?: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [currentAsset, setCurrentAsset] = useState<Asset | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatusState] = useState<string | null>(null);
  const [pendingPlanId, setPendingPlanId] = useState<number | null>(null);
  const [pendingPeriod, setPendingPeriod] = useState<string | null>(null);
  const subStatusRefreshed = useRef(false);

  useEffect(() => {
    // Check for existing auth data on mount
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user');
    const assetsData = localStorage.getItem('assets');
    const currentAssetData = localStorage.getItem('current_asset');
    const orgsData = localStorage.getItem('organizations');
    const currentOrgData = localStorage.getItem('current_organization');

    // Load subscription status
    const storedSubStatus = localStorage.getItem('subscription_status');
    const storedPlanId = localStorage.getItem('subscription_pending_plan');
    const storedPeriod = localStorage.getItem('subscription_pending_period');
    if (storedSubStatus) {
      setSubscriptionStatusState(storedSubStatus);
      if (storedPlanId) setPendingPlanId(parseInt(storedPlanId));
      if (storedPeriod) setPendingPeriod(storedPeriod);
    }

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);

        if (assetsData) {
          const parsedAssets = JSON.parse(assetsData);
          setAssets(parsedAssets);

          if (currentAssetData) {
            const parsedCurrentAsset = JSON.parse(currentAssetData);
            setCurrentAsset(parsedCurrentAsset);
          } else if (parsedAssets.length > 0) {
            setCurrentAsset(parsedAssets[0]);
          }
        }

        // Load organizations from user data or localStorage
        if (parsedUser.organizations && parsedUser.organizations.length > 0) {
          setOrganizations(parsedUser.organizations);
          const defaultOrg = parsedUser.organizations.find((o: Organization) => o.is_default) || parsedUser.organizations[0];
          setCurrentOrganization(defaultOrg);
        } else if (orgsData) {
          const parsedOrgs = JSON.parse(orgsData);
          setOrganizations(parsedOrgs);
          if (currentOrgData) {
            setCurrentOrganization(JSON.parse(currentOrgData));
          } else if (parsedOrgs.length > 0) {
            setCurrentOrganization(parsedOrgs[0]);
          }
        }
      } catch (error) {
        console.error('Error parsing stored auth data:', error);
        logout();
      }

      // Refresh subscription status from API once per session (non-blocking)
      if (!subStatusRefreshed.current) {
        subStatusRefreshed.current = true;
        subscriptionAPI.getCurrent().then(res => {
          const data = res.data?.data;
          const orgStatus = data?.org_status;
          if (!orgStatus) return;

          // Race condition guard: if user just confirmed payment (status was set to 'active'
          // by verifyPayment poll), don't let this background getCurrent() downgrade it
          // back to 'pending_payment'. This can happen when getCurrent() resolves slightly
          // after the webhook has activated the subscription but before the DB reflects it.
          const currentStoredStatus = localStorage.getItem('subscription_status');
          if (currentStoredStatus === 'active' && orgStatus === 'pending_payment') {
            return;
          }

          setSubscriptionStatusState(orgStatus);
          localStorage.setItem('subscription_status', orgStatus);

          // Pending payment: get the pending subscription's plan details
          const pendingSub = data?.pending_subscription;
          if (pendingSub?.plan_id) {
            setPendingPlanId(pendingSub.plan_id);
            localStorage.setItem('subscription_pending_plan', String(pendingSub.plan_id));
          }
          if (pendingSub?.billing_period) {
            setPendingPeriod(pendingSub.billing_period);
            localStorage.setItem('subscription_pending_period', pendingSub.billing_period);
          }

          // Past due: store current subscription plan so gate can show renewal option
          if (orgStatus === 'past_due' && data?.subscription && !pendingSub?.plan_id) {
            const sub = data.subscription;
            if (sub.plan_id) {
              setPendingPlanId(sub.plan_id);
              localStorage.setItem('subscription_pending_plan', String(sub.plan_id));
            }
            if (sub.billing_period) {
              setPendingPeriod(sub.billing_period);
              localStorage.setItem('subscription_pending_period', sub.billing_period);
            }
          }
        }).catch(() => {});
      }
    }

    setIsLoading(false);
  }, []);

  const login = (userData: User, token: string) => {
    setUser(userData);
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setAssets([]);
    setCurrentAsset(null);
    setOrganizations([]);
    setCurrentOrganization(null);
    setSubscriptionStatusState(null);
    setPendingPlanId(null);
    setPendingPeriod(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('assets');
    localStorage.removeItem('current_asset');
    localStorage.removeItem('organizations');
    localStorage.removeItem('current_organization');
    localStorage.removeItem('subscription_status');
    localStorage.removeItem('subscription_pending_plan');
    localStorage.removeItem('subscription_pending_period');
  };

  const setSubscriptionStatus = (status: string | null, planId?: number | null, period?: string | null) => {
    setSubscriptionStatusState(status);
    if (status) {
      localStorage.setItem('subscription_status', status);
    } else {
      localStorage.removeItem('subscription_status');
    }
    if (planId != null) {
      setPendingPlanId(planId);
      localStorage.setItem('subscription_pending_plan', String(planId));
    } else {
      setPendingPlanId(null);
      localStorage.removeItem('subscription_pending_plan');
    }
    if (period) {
      setPendingPeriod(period);
      localStorage.setItem('subscription_pending_period', period);
    } else {
      setPendingPeriod(null);
      localStorage.removeItem('subscription_pending_period');
    }
  };

  const updateAssets = (newAssets: Asset[]) => {
    setAssets(newAssets);
    localStorage.setItem('assets', JSON.stringify(newAssets));
    
    // Set current asset if none is set
    if (!currentAsset && newAssets.length > 0) {
      const firstAsset = newAssets[0];
      setCurrentAsset(firstAsset);
      localStorage.setItem('current_asset', JSON.stringify(firstAsset));
    }
  };

  const updateCurrentAsset = (asset: Asset) => {
    setCurrentAsset(asset);
    localStorage.setItem('current_asset', JSON.stringify(asset));
  };

  const updateOrganizations = (orgs: Organization[]) => {
    setOrganizations(orgs);
    localStorage.setItem('organizations', JSON.stringify(orgs));

    // Set current org if none is set
    if (!currentOrganization && orgs.length > 0) {
      const defaultOrg = orgs.find(o => o.is_default) || orgs[0];
      setCurrentOrganization(defaultOrg);
      localStorage.setItem('current_organization', JSON.stringify(defaultOrg));
    }
  };

  const updateCurrentOrganization = (org: Organization) => {
    setCurrentOrganization(org);
    localStorage.setItem('current_organization', JSON.stringify(org));
  };

  const value: AuthContextType = {
    user,
    assets,
    currentAsset,
    organizations,
    currentOrganization,
    isAuthenticated: !!user,
    isLoading,
    subscriptionStatus,
    pendingPlanId,
    pendingPeriod,
    login,
    logout,
    setAssets: updateAssets,
    setCurrentAsset: updateCurrentAsset,
    setOrganizations: updateOrganizations,
    setCurrentOrganization: updateCurrentOrganization,
    setSubscriptionStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};